import {
  createLocalePlugin,
  createNotificationsPlugin,
  createRulesPlugin,
  createStackPlugin,
  createStoragePlugin,
  createThemePlugin,
  createTooltipPlugin
} from '@vuetify/v0'
import { VueI18nLocaleAdapter } from '@vuetify/v0/locale/adapters/vue-i18n'
import type { App, Plugin } from 'vue'
import type { Composer } from 'vue-i18n'
import { registerComponents } from './components'
import { CookieThemeStorage } from './theme/storage'
import { DEFAULT_THEME, type Theme } from './theme/themes'
import { ruleAliases } from './validators/aliases'

type PluginOptionsOf<T> = T extends (options?: infer O) => Plugin ? O : never

export type UiOptions = {
  components?: boolean
  locale?: PluginOptionsOf<typeof createLocalePlugin>
  rules?: PluginOptionsOf<typeof createRulesPlugin>
  stack?: PluginOptionsOf<typeof createStackPlugin>
  tooltip?: PluginOptionsOf<typeof createTooltipPlugin>
  notifications?: PluginOptionsOf<typeof createNotificationsPlugin>
  theme?: Theme
  fallback?: Theme
  i18n?: { global: Composer }
}

const themePlugins = (app: App, options: UiOptions) => {
  app.use(
    createStoragePlugin({
      adapter: new CookieThemeStorage(undefined, options.theme),
      prefix: '',
      serializer: { read: value => value, write: value => String(value) }
    })
  )

  app.use(
    createThemePlugin({
      themes: { light: { colors: {} }, dark: { dark: true, colors: {} } },
      system: { light: 'light', dark: 'dark' },
      default: options.fallback ?? DEFAULT_THEME,
      target: 'html',
      persist: true
    })
  )
}

export const createUi = (options: UiOptions = {}): Plugin => ({
  install: (app: App) => {
    if (options.components !== false) {
      registerComponents(app)
    }

    app
      .use(createLocalePlugin(options.i18n ? { ...options.locale, adapter: new VueI18nLocaleAdapter(options.i18n) } : options.locale))
      .use(
        createRulesPlugin({
          ...options.rules,
          aliases: { ...ruleAliases(options.i18n?.global.t), ...options.rules?.aliases }
        })
      )
      .use(createStackPlugin(options.stack))
      .use(createTooltipPlugin(options.tooltip))
      .use(createNotificationsPlugin(options.notifications))

    themePlugins(app, options)
  }
})
